import classnames from 'classnames';
import type { SPIntl } from 'contexts/intl/intlContext';
import type { IntlKey } from 'locales';
import type { ProductVariation } from 'models/productsReferential';
import React, { FC, KeyboardEvent, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { getAvailableProductVariations } from 'state/selectors';
import type { AppState } from 'state/state.model';
import type { WorkspaceType } from 'state/workspaces/model';

interface SearchContext {
  searchText: string;
  matchedProductVariations: ProductVariation[];
}

interface SearchResultProps {
  searchContext: SearchContext;
  cursorAtIndex: number;
  setCursorAtIndex: (index: number) => void;
  onClearSearch: () => void;
}

interface ProductSearchInputProps {
  workspaceType: WorkspaceType;
  autoFocus: boolean;
  inputPlaceholder?: IntlKey;
  className?: string;
  onEnter: (selectedProductVariation: ProductVariation) => void;
  onFilter: (
    searchText: string,
    availableProductVariations: readonly ProductVariation[],
    formatMessage: SPIntl['formatMessage'],
  ) => ProductVariation[];
  children: (props: SearchResultProps) => React.JSX.Element;
}

export const ProductSearchInput: FC<ProductSearchInputProps> = ({
  workspaceType,
  autoFocus = false,
  inputPlaceholder,
  className,
  children,
  onEnter,
  onFilter,
}) => {
  const { formatMessage } = useIntl();

  const availableProductVariations = useSelector((state: AppState) =>
    getAvailableProductVariations(state, workspaceType),
  );

  const [cursorAtIndex, setCursorAtIndex] = useState(-1);

  const [searchContext, setSearchContext] = useState<SearchContext>({
    searchText: '',
    matchedProductVariations: [],
  });

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();
      switch (e.key) {
        case 'Enter': {
          onEnter(searchContext.matchedProductVariations[cursorAtIndex]);
          break;
        }
        case 'ArrowDown':
          setCursorAtIndex(index => (index >= searchContext.matchedProductVariations.length - 1 ? index : index + 1));
          break;
        case 'ArrowUp':
          setCursorAtIndex(index => (index <= 0 ? index : index - 1));
          break;
        default:
          break;
      }
    },
    [cursorAtIndex, onEnter, searchContext.matchedProductVariations],
  );

  return (
    <div className={classnames(className, 'd-flex flex-column')}>
      <div className="form-group mb-3">
        <div className="input-group input-group-xl">
          <div className="input-icon-start">
            <em className="icon">search</em>
          </div>
          <input
            autoFocus={autoFocus}
            type="search"
            className="form-control form-control-lg"
            value={searchContext.searchText}
            placeholder={inputPlaceholder === undefined ? undefined : formatMessage({ id: inputPlaceholder })}
            onKeyDown={onKeyDown}
            onChange={({ target: { value } }) => {
              setSearchContext({
                searchText: value,
                matchedProductVariations: onFilter(value, availableProductVariations, formatMessage),
              });
            }}
          />
        </div>
      </div>
      <div className="flex-fill">
        {children({
          searchContext,
          cursorAtIndex,
          setCursorAtIndex,
          onClearSearch: () => setSearchContext({ searchText: '', matchedProductVariations: [] }),
        })}
      </div>
    </div>
  );
};
